import type {
  ADatatableExposedProperties,
  Heading,
  HeadingArgument,
} from '../types'
import defu from 'defu'
import { defineStore } from 'pinia'
import { twMerge } from 'tailwind-merge'

type DatatableProperties = Pick<ADatatableExposedProperties, 'refetch'>

export const useDatatableStore = defineStore('useDatatableStore', () => {
  const datatables = new Map<string, DatatableProperties>()
  const router = useRouter()
  const isRouteReady = ref(false)
  router.beforeResolve(() => {
    isRouteReady.value = true
  })

  const register = (datatableId: string, datatable: DatatableProperties) => {
    datatables.set(datatableId, datatable)
  }

  const unregister = (id: string) => {
    datatables.delete(id)
  }

  const refetch = (
    options?: Partial<{
      ids: string | string[]
      ignoreIds: string[]
    }>,
  ) => {
    const refetchIds = Array.isArray(options?.ids)
      ? options.ids
      : [options?.ids].filter(Boolean)
    const refetchIgnoreIds = options?.ignoreIds || []

    for (const [datatableId, methods] of datatables) {
      if (refetchIds.length && !refetchIds.includes(datatableId))
        continue
      if (refetchIgnoreIds?.includes(datatableId))
        continue
      methods.refetch()
    }
  }

  const headings = (headings: HeadingArgument[]) => {
    const datatableConfig = useAppConfig().datatable
    const headingPresets = structuredClone(
      toRaw(datatableConfig.headingPresets || {}),
    )

    return headings.map((heading) => {
      if (typeof heading === 'string') {
        return headingPresets[heading as keyof typeof headingPresets]
      }

      if (Array.isArray(heading) && heading.length === 2) {
        const [presetKey, settings] = heading
        const preset = headingPresets[presetKey] as Partial<Heading>
        const thClasses = twMerge(preset?.thClasses, settings.thClasses)
        return defu({ thClasses }, settings, headingPresets[presetKey])
      }

      if (typeof heading === 'object') {
        return heading
      }

      return heading
    })
  }

  return {
    datatables,
    headings,
    refetch,
    register,
    unregister,
  }
})
